import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { FaArrowLeft, FaUser, FaEnvelope, FaDollarSign, FaCalendar, FaCheckCircle, FaClock, FaTimesCircle, FaFilter, FaChartBar, FaList, FaPlus, FaTrash } from 'react-icons/fa';
import AdminAPI from '../../utils/adminApi';
import terms from '../../config/terminology';
import { formatCurrency } from '../../utils/formatCurrency';

const AdminDonationsLedger = () => {
  const [donations, setDonations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [pagination, setPagination] = useState({ page: 1, limit: 50, total: 0, pages: 0 });
  const [stats, setStats] = useState({ count: 0, completed_count: 0, pending_count: 0, total_amount: 0, total_fees: 0 });
  const [creators, setCreators] = useState([]);
  const [viewMode, setViewMode] = useState('list'); // 'list' or 'grouped'
  const [groupBy, setGroupBy] = useState('creator');
  const [groupedData, setGroupedData] = useState([]);
  const [showFilters, setShowFilters] = useState(false);
  const [showTestModal, setShowTestModal] = useState(false);
  const [testDonation, setTestDonation] = useState({
    recipient_username: '',
    donor_name: 'Test Donor',
    message: 'Test donation message',
    amount: 10.00
  });
  const [testLoading, setTestLoading] = useState(false);
  const [deleteLoading, setDeleteLoading] = useState(null);

  // Filter states
  const [filters, setFilters] = useState({
    status: '',
    creator_id: '',
    date_from: '',
    date_to: '',
    amount_min: '',
    amount_max: '',
    search: ''
  });

  useEffect(() => {
    fetchCreators();
  }, []);

  useEffect(() => {
    if (viewMode === 'list') {
      fetchDonations();
    } else {
      fetchGroupedData();
    }
    fetchStats();
  }, [pagination.page, filters, viewMode, groupBy]);

  const fetchCreators = async () => {
    try {
      const data = await AdminAPI.get('/api/manage/creators/list');
      setCreators(data.creators);
    } catch (err) {
      console.error('Failed to fetch creators:', err);
    }
  };

  const fetchDonations = async () => {
    try {
      setLoading(true);
      let url = `/api/manage/donations?page=${pagination.page}&limit=${pagination.limit}`;

      if (filters.status) url += `&status_filter=${filters.status}`;
      if (filters.creator_id) url += `&creator_id=${filters.creator_id}`;
      if (filters.date_from) url += `&date_from=${filters.date_from}`;
      if (filters.date_to) url += `&date_to=${filters.date_to}`;
      if (filters.amount_min) url += `&amount_min=${filters.amount_min}`;
      if (filters.amount_max) url += `&amount_max=${filters.amount_max}`;
      if (filters.search) url += `&search=${encodeURIComponent(filters.search)}`;

      const data = await AdminAPI.get(url);
      setDonations(data.donations);
      setPagination(prev => ({ ...prev, ...data.pagination }));
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const fetchGroupedData = async () => {
    try {
      setLoading(true);
      let url = `/api/manage/donations/grouped?group_by=${groupBy}`;

      if (filters.status) url += `&status_filter=${filters.status}`;
      if (filters.date_from) url += `&date_from=${filters.date_from}`;
      if (filters.date_to) url += `&date_to=${filters.date_to}`;

      const data = await AdminAPI.get(url);
      setGroupedData(data.groups);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const fetchStats = async () => {
    try {
      let url = '/api/manage/donations/count?';
      const params = [];

      if (filters.status) params.push(`status_filter=${filters.status}`);
      if (filters.creator_id) params.push(`creator_id=${filters.creator_id}`);
      if (filters.date_from) params.push(`date_from=${filters.date_from}`);
      if (filters.date_to) params.push(`date_to=${filters.date_to}`);
      if (filters.amount_min) params.push(`amount_min=${filters.amount_min}`);
      if (filters.amount_max) params.push(`amount_max=${filters.amount_max}`);
      if (filters.search) params.push(`search=${encodeURIComponent(filters.search)}`);

      url += params.join('&');

      const data = await AdminAPI.get(url);
      setStats(data);
    } catch (err) {
      console.error('Failed to fetch stats:', err);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const formatPeriod = (dateString, groupType) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    if (groupType === 'day') {
      return date.toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
    } else if (groupType === 'week') {
      return `Week of ${date.toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' })}`;
    } else {
      return date.toLocaleDateString('en-US', { year: 'numeric', month: 'long' });
    }
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case 'completed':
        return (
          <span className="inline-flex items-center space-x-1 px-2 py-1 bg-green-500/20 text-green-400 rounded text-sm">
            <FaCheckCircle />
            <span>Completed</span>
          </span>
        );
      case 'pending':
        return (
          <span className="inline-flex items-center space-x-1 px-2 py-1 bg-yellow-500/20 text-yellow-400 rounded text-sm">
            <FaClock />
            <span>Pending</span>
          </span>
        );
      case 'failed':
        return (
          <span className="inline-flex items-center space-x-1 px-2 py-1 bg-red-500/20 text-red-400 rounded text-sm">
            <FaTimesCircle />
            <span>Failed</span>
          </span>
        );
      default:
        return (
          <span className="inline-flex items-center px-2 py-1 bg-gray-500/20 text-gray-400 rounded text-sm">
            {status}
          </span>
        );
    }
  };

  const handleFilterChange = (key, value) => {
    setFilters(prev => ({ ...prev, [key]: value }));
    setPagination(prev => ({ ...prev, page: 1 }));
  };

  const clearFilters = () => {
    setFilters({
      status: '',
      creator_id: '',
      date_from: '',
      date_to: '',
      amount_min: '',
      amount_max: '',
      search: ''
    });
    setPagination(prev => ({ ...prev, page: 1 }));
  };

  const hasActiveFilters = Object.values(filters).some(v => v !== '');

  const handleCreateTestDonation = async () => {
    try {
      setTestLoading(true);
      await AdminAPI.post('/api/manage/test-donation', testDonation);
      setShowTestModal(false);
      setTestDonation({ recipient_username: '', donor_name: 'Test Donor', message: 'Test donation message', amount: 10.00 });
      fetchDonations();
      fetchStats();
    } catch (err) {
      alert('Failed to create test donation: ' + err.message);
    } finally {
      setTestLoading(false);
    }
  };

  const handleDeleteDonation = async (donationId) => {
    if (!window.confirm(`Delete donation #${donationId}? This cannot be undone.`)) return;
    try {
      setDeleteLoading(donationId);
      await AdminAPI.delete(`/api/manage/donations/${donationId}`);
      fetchDonations();
      fetchStats();
    } catch (err) {
      alert('Failed to delete donation: ' + err.message);
    } finally {
      setDeleteLoading(null);
    }
  };

  if (loading && donations.length === 0 && groupedData.length === 0) {
    return (
      <div className="min-h-screen bg-black text-white flex items-center justify-center">
        <div className="text-xl">Loading {terms.donations}...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-black text-white flex items-center justify-center">
        <div className="text-red-500">Error: {error}</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-black text-white p-8">
      <div className="max-w-7xl mx-auto">
        <Link to="/manage" className="inline-flex items-center space-x-2 text-gray-400 hover:text-white mb-6 transition">
          <FaArrowLeft />
          <span>Back to Admin Dashboard</span>
        </Link>

        <div className="mb-8">
          <h1 className="text-3xl font-bold mb-2">{terms.Donations} Ledger</h1>
          <p className="text-gray-400">View all {terms.donations} and tips on the platform</p>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
          <div className="bg-gray-900 p-4 rounded-lg border border-gray-800">
            <p className="text-gray-400 text-sm">Total {terms.Donations}</p>
            <p className="text-2xl font-bold">{stats.count}</p>
          </div>
          <div className="bg-gray-900 p-4 rounded-lg border border-gray-800">
            <p className="text-gray-400 text-sm">Completed</p>
            <p className="text-2xl font-bold text-green-400">{stats.completed_count}</p>
            {stats.pending_count > 0 && (
              <p className="text-xs text-yellow-500 mt-1">{stats.pending_count} pending</p>
            )}
          </div>
          <div className="bg-gray-900 p-4 rounded-lg border border-gray-800">
            <p className="text-gray-400 text-sm">Gross Tips (Owed to Creators)</p>
            <p className="text-2xl font-bold text-blue-400">{formatCurrency(stats.total_amount)}</p>
            <p className="text-xs text-gray-500 mt-1">completed only, 100% owed</p>
          </div>
          <div className="bg-gray-900 p-4 rounded-lg border border-gray-800">
            <p className="text-gray-400 text-sm">Platform Fees</p>
            <p className="text-2xl font-bold text-gray-300">{formatCurrency(stats.total_fees)}</p>
            <p className="text-xs text-gray-500 mt-1">5% + $0.50 each, charged to the donor -- not deducted above</p>
          </div>
        </div>

        {/* View Toggle & Filters Header */}
        <div className="bg-gray-900 rounded-lg border border-gray-800 p-6 mb-6">
          <div className="flex flex-wrap justify-between items-center gap-4 mb-4">
            <div className="flex items-center space-x-4">
              <button
                onClick={() => setViewMode('list')}
                className={`flex items-center space-x-2 px-4 py-2 rounded-lg transition ${
                  viewMode === 'list' ? 'bg-blue-500 text-white' : 'bg-gray-800 text-gray-300 hover:bg-gray-700'
                }`}
              >
                <FaList />
                <span>List View</span>
              </button>
              <button
                onClick={() => setViewMode('grouped')}
                className={`flex items-center space-x-2 px-4 py-2 rounded-lg transition ${
                  viewMode === 'grouped' ? 'bg-blue-500 text-white' : 'bg-gray-800 text-gray-300 hover:bg-gray-700'
                }`}
              >
                <FaChartBar />
                <span>Grouped View</span>
              </button>
            </div>
            <div className="flex items-center space-x-4">
              <button
                onClick={() => setShowFilters(!showFilters)}
                className={`flex items-center space-x-2 px-4 py-2 rounded-lg transition ${
                  showFilters || hasActiveFilters ? 'bg-blue-500/20 text-blue-400' : 'bg-gray-800 text-gray-300 hover:bg-gray-700'
                }`}
              >
                <FaFilter />
                <span>Filters {hasActiveFilters && `(${Object.values(filters).filter(v => v).length})`}</span>
              </button>
              <button
                onClick={() => setShowTestModal(true)}
                className="flex items-center space-x-2 px-4 py-2 bg-green-500/20 text-green-400 hover:bg-green-500/30 rounded-lg transition"
              >
                <FaPlus />
                <span>Test Donation</span>
              </button>
              <button
                onClick={viewMode === 'list' ? fetchDonations : fetchGroupedData}
                className="px-4 py-2 bg-gray-800 hover:bg-gray-700 rounded-lg transition"
              >
                Refresh
              </button>
            </div>
          </div>

          {/* Filters Panel */}
          {showFilters && (
            <div className="border-t border-gray-800 pt-4 mt-4">
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                {/* Search */}
                <div>
                  <label className="block text-sm text-gray-400 mb-1">Search</label>
                  <input
                    type="text"
                    placeholder="Donor name or email..."
                    value={filters.search}
                    onChange={(e) => handleFilterChange('search', e.target.value)}
                    className="w-full px-3 py-2 bg-gray-800 border border-gray-700 rounded-lg text-white placeholder-gray-500"
                  />
                </div>

                {/* Status */}
                <div>
                  <label className="block text-sm text-gray-400 mb-1">Status</label>
                  <select
                    value={filters.status}
                    onChange={(e) => handleFilterChange('status', e.target.value)}
                    className="w-full px-3 py-2 bg-gray-800 border border-gray-700 rounded-lg text-white"
                  >
                    <option value="">All Statuses</option>
                    <option value="completed">Completed</option>
                    <option value="pending">Pending</option>
                    <option value="failed">Failed</option>
                  </select>
                </div>

                {/* Creator */}
                <div>
                  <label className="block text-sm text-gray-400 mb-1">Creator</label>
                  <select
                    value={filters.creator_id}
                    onChange={(e) => handleFilterChange('creator_id', e.target.value)}
                    className="w-full px-3 py-2 bg-gray-800 border border-gray-700 rounded-lg text-white"
                  >
                    <option value="">All Creators</option>
                    {creators.map(c => (
                      <option key={c.id} value={c.id}>
                        {c.name || c.username} (@{c.username})
                      </option>
                    ))}
                  </select>
                </div>

                {/* Date From */}
                <div>
                  <label className="block text-sm text-gray-400 mb-1">Date From</label>
                  <input
                    type="date"
                    value={filters.date_from}
                    onChange={(e) => handleFilterChange('date_from', e.target.value)}
                    className="w-full px-3 py-2 bg-gray-800 border border-gray-700 rounded-lg text-white"
                  />
                </div>

                {/* Date To */}
                <div>
                  <label className="block text-sm text-gray-400 mb-1">Date To</label>
                  <input
                    type="date"
                    value={filters.date_to}
                    onChange={(e) => handleFilterChange('date_to', e.target.value)}
                    className="w-full px-3 py-2 bg-gray-800 border border-gray-700 rounded-lg text-white"
                  />
                </div>

                {/* Amount Min */}
                <div>
                  <label className="block text-sm text-gray-400 mb-1">Min Amount ($)</label>
                  <input
                    type="number"
                    placeholder="0.00"
                    value={filters.amount_min}
                    onChange={(e) => handleFilterChange('amount_min', e.target.value)}
                    className="w-full px-3 py-2 bg-gray-800 border border-gray-700 rounded-lg text-white"
                  />
                </div>

                {/* Amount Max */}
                <div>
                  <label className="block text-sm text-gray-400 mb-1">Max Amount ($)</label>
                  <input
                    type="number"
                    placeholder="0.00"
                    value={filters.amount_max}
                    onChange={(e) => handleFilterChange('amount_max', e.target.value)}
                    className="w-full px-3 py-2 bg-gray-800 border border-gray-700 rounded-lg text-white"
                  />
                </div>

                {/* Clear Filters */}
                <div className="flex items-end">
                  <button
                    onClick={clearFilters}
                    disabled={!hasActiveFilters}
                    className="w-full px-4 py-2 bg-red-500/20 text-red-400 rounded-lg hover:bg-red-500/30 transition disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    Clear Filters
                  </button>
                </div>
              </div>

              {/* Group By (only in grouped view) */}
              {viewMode === 'grouped' && (
                <div className="mt-4 pt-4 border-t border-gray-800">
                  <label className="block text-sm text-gray-400 mb-2">Group By</label>
                  <div className="flex flex-wrap gap-2">
                    {['creator', 'status', 'day', 'week', 'month'].map(option => (
                      <button
                        key={option}
                        onClick={() => setGroupBy(option)}
                        className={`px-4 py-2 rounded-lg transition capitalize ${
                          groupBy === option
                            ? 'bg-blue-500 text-white'
                            : 'bg-gray-800 text-gray-300 hover:bg-gray-700'
                        }`}
                      >
                        {option}
                      </button>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
        </div>

        {/* List View */}
        {viewMode === 'list' && (
          <div className="bg-gray-900 rounded-lg border border-gray-800 p-6">
            <h2 className="text-xl font-semibold mb-4">All {terms.Donations}</h2>

            {donations.length === 0 ? (
              <div className="text-center py-8 text-gray-500">
                No {terms.donations} found
              </div>
            ) : (
              <>
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead>
                      <tr className="border-b border-gray-800">
                        <th className="text-left p-4">ID</th>
                        <th className="text-left p-4">Date</th>
                        <th className="text-left p-4">From</th>
                        <th className="text-left p-4">To</th>
                        <th className="text-left p-4">Tip (Owed)</th>
                        <th className="text-left p-4 text-gray-500">Platform Fee*</th>
                        <th className="text-left p-4">Status</th>
                        <th className="text-left p-4">Message</th>
                        <th className="text-left p-4"></th>
                      </tr>
                    </thead>
                    <tbody>
                      {donations.map((donation) => (
                        <tr key={donation.id} className="border-b border-gray-800 hover:bg-gray-800/50">
                          <td className="p-4 text-gray-400 font-mono text-sm">#{donation.id}</td>
                          <td className="p-4">
                            <div className="flex items-center space-x-2">
                              <FaCalendar className="text-gray-500" />
                              <span className="text-sm">{formatDate(donation.created_at)}</span>
                            </div>
                          </td>
                          <td className="p-4">
                            <div>
                              <div className="flex items-center space-x-2">
                                <FaUser className="text-gray-500" />
                                <span>{donation.name || 'Anonymous'}</span>
                              </div>
                              {donation.donor_email && (
                                <div className="flex items-center space-x-2 mt-1">
                                  <FaEnvelope className="text-gray-500 text-xs" />
                                  <span className="text-xs text-gray-400">{donation.donor_email}</span>
                                </div>
                              )}
                            </div>
                          </td>
                          <td className="p-4">
                            <div>
                              <span className="font-medium">{donation.recipient_name || 'Unknown'}</span>
                              {donation.recipient_username && (
                                <span className="text-gray-400 text-sm ml-1">@{donation.recipient_username}</span>
                              )}
                            </div>
                          </td>
                          <td className="p-4">
                            <div className="flex items-center space-x-2">
                              <FaDollarSign className="text-green-500" />
                              <span className="font-semibold">{formatCurrency(donation.amount)}</span>
                            </div>
                          </td>
                          <td className="p-4 text-gray-500">
                            {formatCurrency(donation.platform_fee ?? 0)}
                          </td>
                          <td className="p-4">
                            {getStatusBadge(donation.status)}
                          </td>
                          <td className="p-4">
                            <span className="text-gray-400 text-sm truncate block max-w-[200px]" title={donation.message}>
                              {donation.message || '-'}
                            </span>
                          </td>
                          <td className="p-4 whitespace-nowrap">
                            <button
                              onClick={() => handleDeleteDonation(donation.id)}
                              disabled={deleteLoading === donation.id}
                              className="p-2 text-red-400 hover:text-red-300 hover:bg-red-500/20 rounded transition disabled:opacity-50"
                              title="Delete donation"
                            >
                              <FaTrash size={16} />
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                <p className="text-xs text-gray-500 p-4">
                  * Platform fee is charged to the donor on top of the tip -- informational only, never deducted from what the creator is owed.
                </p>

                {/* Pagination */}
                {pagination.pages > 1 && (
                  <div className="flex justify-center items-center space-x-4 mt-6">
                    <button
                      onClick={() => setPagination(prev => ({ ...prev, page: prev.page - 1 }))}
                      disabled={pagination.page === 1}
                      className="px-4 py-2 bg-gray-800 hover:bg-gray-700 rounded-lg transition disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      Previous
                    </button>
                    <span className="text-gray-400">
                      Page {pagination.page} of {pagination.pages}
                    </span>
                    <button
                      onClick={() => setPagination(prev => ({ ...prev, page: prev.page + 1 }))}
                      disabled={pagination.page >= pagination.pages}
                      className="px-4 py-2 bg-gray-800 hover:bg-gray-700 rounded-lg transition disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      Next
                    </button>
                  </div>
                )}
              </>
            )}
          </div>
        )}

        {/* Grouped View */}
        {viewMode === 'grouped' && (
          <div className="bg-gray-900 rounded-lg border border-gray-800 p-6">
            <h2 className="text-xl font-semibold mb-4">
              {terms.Donations} by {groupBy.charAt(0).toUpperCase() + groupBy.slice(1)}
            </h2>

            {groupedData.length === 0 ? (
              <div className="text-center py-8 text-gray-500">
                No data found
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b border-gray-800">
                      <th className="text-left p-4">
                        {groupBy === 'creator' ? 'Creator' :
                         groupBy === 'status' ? 'Status' : 'Period'}
                      </th>
                      <th className="text-left p-4">Count</th>
                      <th className="text-left p-4">Gross Tips (Owed)</th>
                      <th className="text-left p-4 text-gray-500">Platform Fees*</th>
                    </tr>
                  </thead>
                  <tbody>
                    {groupedData.map((group, index) => (
                      <tr key={index} className="border-b border-gray-800 hover:bg-gray-800/50">
                        <td className="p-4">
                          {groupBy === 'creator' ? (
                            <div>
                              <span className="font-medium">{group.creator_name || 'Unknown'}</span>
                              {group.creator_username && (
                                <span className="text-gray-400 text-sm ml-1">@{group.creator_username}</span>
                              )}
                            </div>
                          ) : groupBy === 'status' ? (
                            getStatusBadge(group.status)
                          ) : (
                            <span>{formatPeriod(group.period, groupBy)}</span>
                          )}
                        </td>
                        <td className="p-4">
                          <span className="font-semibold">{group.count}</span>
                        </td>
                        <td className="p-4">
                          <span className="font-semibold text-blue-400">{formatCurrency(group.total_amount)}</span>
                        </td>
                        <td className="p-4 text-gray-500">
                          {formatCurrency(group.total_fees ?? 0)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                  <tfoot>
                    <tr className="border-t-2 border-gray-700 bg-gray-800/50">
                      <td className="p-4 font-semibold">Total</td>
                      <td className="p-4 font-semibold">
                        {groupedData.reduce((sum, g) => sum + g.count, 0)}
                      </td>
                      <td className="p-4 font-semibold text-blue-400">
                        {formatCurrency(groupedData.reduce((sum, g) => sum + g.total_amount, 0))}
                      </td>
                      <td className="p-4 font-semibold text-gray-500">
                        {formatCurrency(groupedData.reduce((sum, g) => sum + (g.total_fees ?? 0), 0))}
                      </td>
                    </tr>
                  </tfoot>
                </table>
                <p className="text-xs text-gray-500 p-4">
                  * Platform fee is charged to the donor on top of the tip -- informational only, never deducted from what's owed.
                </p>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Test Donation Modal */}
      {showTestModal && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50">
          <div className="bg-gray-900 border border-gray-700 rounded-lg p-6 w-full max-w-md">
            <h3 className="text-xl font-semibold mb-4">Create Test Donation</h3>

            <div className="space-y-4">
              <div>
                <label className="block text-sm text-gray-400 mb-1">Recipient</label>
                <select
                  value={testDonation.recipient_username}
                  onChange={(e) => setTestDonation(prev => ({ ...prev, recipient_username: e.target.value }))}
                  className="w-full px-3 py-2 bg-gray-800 border border-gray-700 rounded-lg text-white"
                >
                  <option value="">Select a creator...</option>
                  {creators.map(c => (
                    <option key={c.id} value={c.username}>
                      {c.name || c.username} (@{c.username})
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm text-gray-400 mb-1">Donor Name</label>
                <input
                  type="text"
                  value={testDonation.donor_name}
                  onChange={(e) => setTestDonation(prev => ({ ...prev, donor_name: e.target.value }))}
                  className="w-full px-3 py-2 bg-gray-800 border border-gray-700 rounded-lg text-white"
                />
              </div>

              <div>
                <label className="block text-sm text-gray-400 mb-1">Message</label>
                <textarea
                  value={testDonation.message}
                  onChange={(e) => setTestDonation(prev => ({ ...prev, message: e.target.value }))}
                  className="w-full px-3 py-2 bg-gray-800 border border-gray-700 rounded-lg text-white"
                  rows={3}
                />
              </div>

              <div>
                <label className="block text-sm text-gray-400 mb-1">Amount ($)</label>
                <input
                  type="number"
                  min="1"
                  step="0.01"
                  value={testDonation.amount}
                  onChange={(e) => setTestDonation(prev => ({ ...prev, amount: parseFloat(e.target.value) || 0 }))}
                  className="w-full px-3 py-2 bg-gray-800 border border-gray-700 rounded-lg text-white"
                />
              </div>
            </div>

            <div className="flex justify-end space-x-3 mt-6">
              <button
                onClick={() => setShowTestModal(false)}
                className="px-4 py-2 bg-gray-800 hover:bg-gray-700 rounded-lg transition"
              >
                Cancel
              </button>
              <button
                onClick={handleCreateTestDonation}
                disabled={testLoading || !testDonation.recipient_username}
                className="px-4 py-2 bg-green-600 hover:bg-green-500 rounded-lg transition disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {testLoading ? 'Creating...' : 'Create'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminDonationsLedger;